"use client";

// Fetches the signed-in user's own applications across every resource type.
// The backend queryset (AuditedModelViewSet.get_queryset in registry/views.py)
// already scopes non-staff users to their own submissions, so this is just a
// frontend fetch that wasn't happening before — the dashboard and
// /applications previously only ever read localStorage. Falls back to the
// local store unchanged when no backend is configured (offline demo mode).
import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "./api";
import { fetchAllRemote, remoteToApplication, TYPE_ENDPOINT } from "./backendSync";
import { Application } from "./types";
import { useStore } from "./store";

const ALL_TYPES = Object.keys(TYPE_ENDPOINT) as (keyof typeof TYPE_ENDPOINT)[];

export function useMyApplications() {
  const localStore = useStore();
  const [remoteApplications, setRemoteApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(!!API_BASE);

  const refresh = useCallback(async () => {
    if (!API_BASE) return;
    setLoading(true);
    const lists = await Promise.all(ALL_TYPES.map((t) => fetchAllRemote(t)));
    const merged = lists
      .flatMap((items, i) => items.map((item) => remoteToApplication(ALL_TYPES[i], item)))
      .sort((a, b) => b.createdAt - a.createdAt);
    setRemoteApplications(merged);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!API_BASE) {
    return { applications: localStore.applications, loading: false, refresh: async () => {} };
  }
  return { applications: remoteApplications, loading, refresh };
}
